/**
 * Authentication Recovery Utilities
 * Handles recovery from corrupted auth state and provides fallback mechanisms
 */

import { TokenManager } from './storage/tokenmanager.storage';
import { store } from '../store';
import { logout, setUser } from '../features/auth/slices/auth.slice';
import { getProfile } from '../features/auth/thunks/auth.thunk';

export class AuthRecovery {
  private static recoveryInProgress = false;
  private static lastRecoveryAttempt = 0;
  private static readonly RECOVERY_COOLDOWN = 30000; // 30 seconds

  /**
   * Attempt to recover from corrupted authentication state
   */
  static async attemptRecovery(): Promise<boolean> {
    if (this.recoveryInProgress) {
      return false;
    }

    const now = Date.now();
    if (now - this.lastRecoveryAttempt < this.RECOVERY_COOLDOWN) {
      return false;
    }

    this.recoveryInProgress = true;
    this.lastRecoveryAttempt = now;

    try {      
      const token = TokenManager.getAccessToken();
      if (token && TokenManager.isTokenCorrupted(token)) {
        console.warn('Corrupted token detected, clearing storage');
        await TokenManager.clearTokens();
        store.dispatch(logout());
        return false;
      }

      if (token && TokenManager.getRefreshToken()) {
        try {
          await TokenManager.validateAndRefreshIfNeeded();
          
          if (TokenManager.isAuthenticated()) {
            const user = TokenManager.getUser();
            if (user) {
              store.dispatch(setUser(user));
              return true;
            } else {
              // Try to fetch user profile
              try {
                await store.dispatch(getProfile()).unwrap();
                return true;
              } catch (profileError) {
                console.warn('Profile fetch failed during recovery:', profileError);
                await TokenManager.clearTokens();
                store.dispatch(logout());
                return false;
              }
            }
          } else {
            await TokenManager.clearTokens();
            store.dispatch(logout());
            return false;
          }
        } catch (validationError) {
          console.error('Token validation failed during recovery:', validationError);
          await TokenManager.clearTokens();
          store.dispatch(logout());
          return false;
        }
      }

      store.dispatch(logout());
      return false;

    } catch (error) {
      console.error('Auth recovery failed:', error);
      // Emergency cleanup
      try {
        await TokenManager.clearTokens();
        store.dispatch(logout());
      } catch (cleanupError) {
        console.error('Emergency cleanup failed:', cleanupError);
      }
      return false;
    } finally {
      this.recoveryInProgress = false;
    }
  }

  /**
   * Check if auth state appears corrupted
   */
  static isAuthStateCorrupted(): boolean {
    try {
      const state = store.getState();
      const authState = state.auth;

      // Check for logical inconsistencies
      const hasTokens = !!(TokenManager.getAccessToken() && TokenManager.getRefreshToken());
      const isAuthenticated = TokenManager.isAuthenticated();
      const reduxAuthenticated = authState.isAuthenticated;
      const hasUser = !!authState.user;

      if (hasTokens && !reduxAuthenticated) return true;
      if (reduxAuthenticated && !hasUser) return true;
      if (hasTokens && TokenManager.isTokenCorrupted(TokenManager.getAccessToken()!)) return true;

      return false;
    } catch (error) {
      console.error('Error checking auth state corruption:', error);
      return true; // Assume corrupted if we can't check
    }
  }

  /**
   * Periodic health check for auth state
   */
  static async performHealthCheck(): Promise<void> {
    if (this.isAuthStateCorrupted()) {
      console.warn('Auth state corruption detected, attempting recovery');
      await this.attemptRecovery();
    }
  }

  /**
   * Force clear all auth state (emergency use only)
   */
  static async forceClearAuthState(): Promise<void> {
    console.warn('Force clearing auth state (emergency measure)');
    try {
      await TokenManager.clearTokens();
      store.dispatch(logout());
      
      // Force reload to clear any remaining in-memory state
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Force clear failed:', error);
    }
  }

  /**
   * Get diagnostic information about auth state
   */
  static getAuthDiagnostics(): {
    hasAccessToken: boolean;
    hasRefreshToken: boolean;
    tokenExpired: boolean;
    tokenCorrupted: boolean;
    reduxAuthenticated: boolean;
    hasUser: boolean;
    lastTokenCheck: number;
    isConsistent: boolean;
  } {
    const token = TokenManager.getAccessToken();
    const refreshToken = TokenManager.getRefreshToken();
    const state = store.getState();
    const authState = state.auth;

    return {
      hasAccessToken: !!token,
      hasRefreshToken: !!refreshToken,
      tokenExpired: token ? TokenManager.isTokenExpired(token) : false,
      tokenCorrupted: token ? TokenManager.isTokenCorrupted(token) : false,
      reduxAuthenticated: authState.isAuthenticated,
      hasUser: !!authState.user,
      lastTokenCheck: authState.lastTokenCheck || 0,
      isConsistent: !this.isAuthStateCorrupted(),
    };
  }
}

// Export singleton instance for easier usage
export const authRecovery = AuthRecovery;
